import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatDividerModule } from "@angular/material/divider";
import { MatIconModule } from "@angular/material/icon";
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatToolbarModule } from "@angular/material/toolbar";

import { RecordData } from "src/app/types";
import { translateEventType } from "../../measured-data-details-labels";
import { RecordingEvent } from "../../measured-data-details.types";

@Component({
  selector: "app-measured-data-wave-card",
  templateUrl: "./measured-data-wave-card.component.html",
  styleUrls: ["./measured-data-wave-card.component.scss"],
  imports: [
    CommonModule,
    MatCardModule,
    MatToolbarModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDividerModule,
    MatProgressBarModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasuredDataWaveCardComponent {
  readonly playingStopLabel = $localize`:@@home.waveCard.ariaLabel.stop:停止`;
  readonly playingPlayLabel = $localize`:@@home.waveCard.ariaLabel.play:再生`;
  readonly defaultWaveErrorMessage = $localize`:@@home.waveCard.error.cannotFetchAudio:音声ファイルを取得できません`;

  @Input() recordData: RecordData | null = null;
  @Input() recordtype: number | null = null;
  @Input() recordLoadingSuccess = false;
  @Input() waveLoadingPercent: number | null = null;
  @Input() waveProcessing = false;
  @Input() waveDownloading = false;
  @Input() waveDownloadPercent: number | null = null;
  @Input() waveErrorMessage: string | null = null;
  @Input() selectedEvent: RecordingEvent | null = null;
  @Input() currentStartOffset = 0;
  @Input() playing = false;
  @Input() timelineOffsetReference = 0;
  @Input() zoomValue = 0;
  @Input() frameless = false;
  @Input() showMiniPlayback = false;
  @Input() allowVideoModePlayback = false;
  /** AvSync 前方トリム overlay の幅比率（0..1）。0 で非表示。 */
  @Input() avSyncFrontTrimRatio = 0;
  /** AvSync 末尾トリム overlay の幅比率（0..1）。0 で非表示。 */
  @Input() avSyncEndTrimRatio = 0;

  @Output() offsetToggle = new EventEmitter<boolean>();
  @Output() seek = new EventEmitter<number>();
  @Output() playPauseClicked = new EventEmitter<void>();
  @Output() zoomMinusClicked = new EventEmitter<void>();
  @Output() zoomResetClicked = new EventEmitter<void>();
  @Output() zoomPlusClicked = new EventEmitter<void>();

  @ViewChild("waveDiv") private waveDiv?: ElementRef<HTMLDivElement>;

  get waveContainer(): HTMLElement | null {
    return this.waveDiv?.nativeElement ?? null;
  }

  get canShowToolbar(): boolean {
    return this.shouldDisplayAudioControls();
  }

  get shouldShowTimelineControls(): boolean {
    return this.hasWaveRenderableData();
  }

  get showOffsetToggle(): boolean {
    return (
      this.shouldShowTimelineControls &&
      this.recordData?.data?.recordType === "RSST" &&
      (this.timelineOffsetReference ?? 0) > 0
    );
  }

  get isVideoMode(): boolean {
    return this.recordtype === 4 || this.recordData?.data?.recordType === "VS";
  }

  private shouldDisplayAudioControls(): boolean {
    if (this.isVideoMode) {
      return this.allowVideoModePlayback && this.hasWaveRenderableData();
    }
    return this.hasWaveRenderableData();
  }

  private hasWaveRenderableData(): boolean {
    if (!this.recordData) {
      return false;
    }
    if (this.recordData.id === "new_gost") {
      return false;
    }
    if (
      this.recordtype === 7 &&
      (this.recordData.data?.totalTime === "00:00:00" ||
        Number(this.recordData.data?.wavDuration ?? 0) <= 0)
    ) {
      return false;
    }
    const storage = this.recordData.data?.storage;
    const audioSize = storage?.audioSize ?? this.recordData.data?.audioSize;
    if (
      this.recordtype === 7 &&
      !this.hasStorageBlobResource(storage?.audio, audioSize)
    ) {
      return false;
    }
    return this.recordtype !== -1;
  }

  private hasStorageBlobResource(url: unknown, size: unknown): boolean {
    if (size === null) {
      return false;
    }
    if (size !== undefined) {
      const numericSize = Number(size);
      if (Number.isFinite(numericSize)) {
        return numericSize > 0;
      }
    }
    return typeof url === "string" ? url.trim().length > 0 : !!url;
  }

  getEventTypeLabel(type: string): string {
    return translateEventType(type);
  }
}
